// src/components/admin/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { formatEurCheckout } from '../../lib/currency';

const S_COLORS = { pending:'#f0a500', paid:'#4cca74', processing:'#4a9edd', shipped:'#4a9edd', delivered:'#4cca74', cancelled:'#e05050' };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ revenue:0, orders:0, customers:0, pending:0 });
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    Promise.all([
      supabase.from('orders').select('total_eur,status').gte('created_at', today.toISOString()),
      supabase.from('profiles').select('id', { count:'exact' }).eq('role','customer'),
      supabase.from('orders').select('*').order('created_at', { ascending:false }).limit(8),
    ]).then(([ord, cust, recent]) => {
      const orders = ord.data || [];
      setStats({ revenue: orders.filter(o=>o.status==='paid').reduce((s,o)=>s+Number(o.total_eur),0), orders:orders.length, customers:cust.count||0, pending:orders.filter(o=>o.status==='pending').length });
      setRecentOrders(recent.data || []);
    }).catch(err => {
      console.error('[AdminDashboard] Load error:', err.message);
    });
  }, []);

  const tiles = [
    { icon:'📦', label:'Products',   sub:'Manage catalogue',  path:'/admin/products', green:true },
    { icon:'🛒', label:'Orders',     sub:'View & process',    path:'/admin/orders' },
    { icon:'📝', label:'Blog',       sub:'Write & publish',   path:'/admin/blog' },
    { icon:'🖼️', label:'Slides',    sub:'Hero slideshow',    path:'/admin/slides' },
    { icon:'🎟️', label:'Discounts', sub:'Codes & coupons',   path:'/admin/discounts' },
    { icon:'🎰', label:'Wheel',      sub:'Reward wheel',      path:'/admin/wheel' },
    { icon:'⚖️', label:'Compliance', sub:'Rules & audit log', path:'/admin/compliance' },
    { icon:'✉️', label:'Newsletter', sub:'Send campaigns',    path:'/admin/newsletter' },
    { icon:'👥', label:'Customers',  sub:'User management',   path:'/admin/customers' },
    { icon:'📊', label:'Analytics',  sub:'Revenue & traffic', path:'/admin/analytics' },
  ];

  return (
    <div className="page-container" style={{ padding:'32px 24px' }}>
      <h2 style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'2rem', letterSpacing:'.05em', marginBottom:24 }}>Admin Dashboard</h2>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12, marginBottom:28 }}>
        {[
          { label:'Revenue today', value:formatEurCheckout(stats.revenue), green:true },
          { label:'Orders today',  value:stats.orders, sub:`${stats.pending} pending` },
          { label:'Customers',     value:stats.customers },
          { label:'Pending',       value:stats.pending, amber:true },
        ].map((s,i) => (
          <div key={i} style={{ background:'#111', borderRadius:14, padding:'18px 20px', border:'1px solid rgba(255,255,255,.07)' }}>
            <div style={{ fontSize:'.7rem', color:'#555', fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:'.1em', textTransform:'uppercase', marginBottom:6 }}>{s.label}</div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.8rem', letterSpacing:'.04em', color: s.green?'#4cca74':s.amber?'#f0a500':'#f4f4f4' }}>{s.value}</div>
            {s.sub && <div style={{ fontSize:'.72rem', color:'#555', marginTop:2 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:12, marginBottom:32 }}>
        {tiles.map(tile => (
          <button key={tile.path} onClick={() => navigate(tile.path)} style={{ background: tile.green?'rgba(76,202,116,.08)':'#111', border:`1px solid ${tile.green?'rgba(76,202,116,.25)':'rgba(255,255,255,.07)'}`, borderRadius:14, padding:'20px', textAlign:'left', cursor:'pointer', transition:'transform .2s' }}
            onMouseOver={e => e.currentTarget.style.transform='translateY(-2px)'}
            onMouseOut={e  => e.currentTarget.style.transform='translateY(0)'}
          >
            <div style={{ fontSize:'1.8rem', marginBottom:8 }}>{tile.icon}</div>
            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.92rem', fontWeight:700, letterSpacing:'.05em', textTransform:'uppercase', color: tile.green?'#4cca74':'#f4f4f4', marginBottom:3 }}>{tile.label}</div>
            <div style={{ fontSize:'.75rem', color:'#555' }}>{tile.sub}</div>
          </button>
        ))}
      </div>

      <div style={{ background:'#111', borderRadius:16, border:'1px solid rgba(255,255,255,.07)', overflow:'hidden' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,.07)', fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.2em', textTransform:'uppercase', color:'#4cca74' }}>Recent Orders</div>
        {recentOrders.map(order => (
          <button key={order.id} onClick={() => navigate('/admin/orders')} style={{ width:'100%', background:'none', border:'none', borderBottom:'1px solid rgba(255,255,255,.04)', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', cursor:'pointer' }}
            onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,.02)'}
            onMouseOut={e  => e.currentTarget.style.background='none'}
          >
            <div style={{ textAlign:'left' }}>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.88rem', color:'#ccc', fontWeight:600 }}>#{order.id.slice(0,8).toUpperCase()}</div>
              <div style={{ fontSize:'.75rem', color:'#555' }}>{order.customer_name} · {new Date(order.created_at).toLocaleDateString()}</div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.1rem', color:'#4cca74' }}>{formatEurCheckout(order.total_eur)}</div>
              <div style={{ fontSize:'.7rem', color:S_COLORS[order.status]||'#888', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase' }}>{order.status}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}